import React, { useMemo, useCallback } from "react"
import {
  Avatar,
  Box,
  Card,
  CardMedia,
  Divider,
  Stack,
  Typography,
  useTheme,
  Chip,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import { useNavigate } from "react-router-dom"
import get from "lodash/get"

import { getIcon } from "utils/icon"
import { getCategoryIcon, getFeatureDetail } from "utils/feature"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"

import { StoreIcon } from "components/icons"

export const DirectoryOccupantCard = ({
  data,
  sx,
  endAdornmentSlotClasses,
  endAdornmentSlots,
  ...props
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isMultiVenueProject } = useVenue()
  const categoryIcon = useMemo(() => {
    const parentCategory = get(data, "properties.local_categories[0]", {})
    return getCategoryIcon(parentCategory)
  }, [data])
  const { name, logo, group, category, level, venueLogo, boothName, isAd } =
    useMemo(() => getFeatureDetail(data), [data])

  const showVenueLogo =
    isMultiVenueProject && venueLogo && !data.hasMultipleLocations

  const handleClick = useCallback(
    () => navigate(`/maps/place/${data.id}`),
    [navigate, data?.id]
  )

  const theme = useTheme()
  const { styleOverrides = {} } =
    theme.components?.[`DirectoryOccupantCard`] || {}
  const {
    root,
    occupantLogo,
    cardContent,
    levelContainer,
    divider,
    venueLogo: venueLogoStyle,
    titleText,
    groupText,
    categoryText,
    titleContainer,
    detailWrapper,
    levelContainerWrapper,
    locationInfoContainer,
    boothText,
    infoContainer,
    chip,
  } = styleOverrides

  return (
    // TODO:: Render collapse item when hasMultipleLocations (has same name.en)
    <Card sx={deepmerge(root, sx)} onClick={handleClick} {...props}>
      <Box sx={occupantLogo}>
        <Avatar src={logo}>
          {getIcon(categoryIcon, null, {
            imageIconProps: {
              solidColorFilterId: "directoryCard_avatar_filter",
            },
          }) || <StoreIcon />}
        </Avatar>
      </Box>
      <Box sx={cardContent}>
        <Box sx={titleContainer}>
          <Stack direction="row" justifyContent="space-between">
            <Typography sx={titleText}>{t(name)}</Typography>
            {isAd && (
              <Chip
                variant="contained"
                label={t("sponsored.search_result")}
                sx={chip}
              />
            )}
          </Stack>
          {boothName && (
            <Typography sx={boothText}>
              {`${t("Booth")} ${t(boothName)}`}
            </Typography>
          )}
        </Box>
        <Stack spacing={1} sx={detailWrapper}>
          <Divider sx={divider} />
          <Stack
            direction="row"
            sx={infoContainer}
            justifyContent="space-between"
          >
            <Stack justifyContent="end" spacing={1} sx={locationInfoContainer}>
              {showVenueLogo && (
                <CardMedia
                  component="img"
                  image={!data.hasMultipleLocations && venueLogo}
                  sx={venueLogoStyle}
                />
              )}
              <Typography variant="body2" color="grey" sx={groupText}>
                {t(group)}
              </Typography>
              <Typography variant="body2" sx={categoryText}>
                {t(category)}
              </Typography>
            </Stack>
            <Box sx={levelContainerWrapper}>
              <Box sx={levelContainer}>
                <Typography variant="h6">
                  {t(data.hasMultipleLocations ? "Multiple Locations" : level)}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Stack>
      </Box>
    </Card>
  )
}
